import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { networkManager } from '../services/NetworkManager';
import { Definition } from '../types';
import { useNotification } from '../contexts/NotificationContext';

const DefinitionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloadingProto, setDownloadingProto] = useState<string | null>(null);

  useEffect(() => {
    loadDefinitions();
  }, []);

  const loadDefinitions = async () => {
    try {
      setLoading(true);
      const data = await networkManager.getDefinitions();
      setDefinitions(data || []);
    } catch (err) {
      console.error('Error loading definitions:', err);
      showNotification('Failed to load definitions', 'error', 8000);
      setDefinitions([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    // Parse the date string and treat it as UTC
    const date = new Date(dateString);
    
    // Get UTC components to avoid timezone conversion issues
    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    
    return `${day}/${month}/${year} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const getDefinitionStatus = (definition: Definition) => {
    if (definition.deprecated) return 'deprecated';
    return 'active';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <div title="Active">
            <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'deprecated':
        return (
          <div title="Deprecated">
            <svg className="h-5 w-5 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const handleAddDefinition = () => {
    navigate('/definitions/add');
  };

  const handleDownloadProto = async (definitionId: string, definitionTitle: string) => {
    try {
      setDownloadingProto(definitionId);
      await networkManager.downloadProtoFile(definitionId, definitionTitle);
      showNotification(`Proto file for "${definitionTitle}" downloaded successfully`, 'success', 5000);
    } catch (error: any) {
      console.error('Error downloading proto file:', error);
      showNotification(
        error.message || 'Failed to download proto file',
        'error',
        8000
      );
    } finally {
      setDownloadingProto(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading definitions...</div>
      </div>
    );
  }



  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Definitions</h1>
            <p className="text-gray-600 mt-1">Manage your data definitions and configurations</p>
          </div>
          <button 
            onClick={handleAddDefinition}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-blue-600 hover:border-blue-700 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 transform hover:scale-105"
          >
            <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Definition
          </button>
        </div>
      </div>



      {/* Table */}
      <div className="table-container">
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th className="w-48">Title</th>
                <th className="w-64">Description</th>
                <th className="w-32">Author</th>
                <th className="w-40">Created At</th>
                <th className="w-32">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!definitions || definitions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <svg className="h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-gray-500 text-lg font-medium">No definitions found</p>
                      <p className="text-gray-400 text-sm">Get started by creating your first definition</p>
                    </div>
                  </td>
                </tr>
              ) : (
                (definitions || []).map((definition) => {
                  const status = getDefinitionStatus(definition);
                  return (
                    <tr key={definition._id}>
                      <td>
                        <div className="flex items-center space-x-2">
                          {getStatusIcon(status)}
                          <div>
                            <Link
                              to={`/definitions/${definition._id}`}
                              className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors"
                              title={definition.title}
                            >
                              {definition.title}
                            </Link>
                            <p className="text-sm text-gray-500">ID: {definition._id}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="max-w-xs">
                          <p className="text-sm text-gray-900 truncate" title={definition.description}>
                            {definition.description || 'No description provided'}
                          </p>
                          {definition.definitionURL && (
                            <p className="text-xs text-gray-500 truncate" title={definition.definitionURL}>
                              URL: {' '}
                              <a 
                                href={definition.definitionURL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {definition.definitionURL.substring(0, 20)}...
                              </a>
                            </p>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <p className="text-sm text-gray-600">{definition.author?.name || 'Unknown'}</p>
                        </div>
                      </td>
                      <td>
                        <div>
                          <p className="text-sm text-gray-600">{formatDate(definition.createdAt)}</p>
                          <p className={`text-xs ${definition.deprecated ? 'text-red-500' : 'text-green-500'}`}>
                            Deprecated: {definition.deprecated ? 'Yes' : 'No'}
                          </p>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleDownloadProto(definition._id, definition.title)}
                            disabled={downloadingProto === definition._id}
                            className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                              !downloadingProto || downloadingProto !== definition._id
                                ? 'text-blue-700 hover:text-blue-800 hover:bg-blue-50 border border-blue-200 hover:border-blue-300'
                                : 'text-gray-400 cursor-not-allowed'
                            }`}
                            title="Download Proto File"
                          >
                            {downloadingProto === definition._id ? (
                              <svg className="h-4 w-4 animate-spin mr-1.5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            ) : (
                              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            )}
                            {downloadingProto === definition._id ? 'Downloading...' : 'Download Proto'}
                          </button>
                          

                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DefinitionsPage;
